import FilterPills from 'components/Table/FilterPills';
import LoadingButton from '@enerex/unified-ui-react/LoadingButton';
import TableSearch from 'components/Table/TableSearch';
import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Card,
  Form,
  Dropdown,
} from "react-bootstrap";
import ConfirmationModal from "@enerex/unified-ui-react/ConfirmationModal";
import { DEV_URL, rdelete, rget, rpost, rput } from "../../../utils/requests";
import { trimInvalidUTCDate } from "../../../helpers/api/utils";
import Select from "components/UnifiedSelect";
import FormInput from "components/FormInput/index";
import Table from "@enerex/unified-ui-react/Table";
import FileUploader from "components/UnifiedFileUploader";
import useShowSnackbar from "../../../hooks/useShowSnackbar";
import { buildCompanyLogoUrl } from "../../../utils/logo";
import { useSearch } from "@enerex/unified-ui-react/hooks";

const BRAND_FILTER_OPTIONS = [
  { value: "all", label: "All" },
  { value: "assigned", label: "Assigned" },
  { value: "unassigned", label: "Unassigned" },
];

const STATUS_OPTIONS = [
  { value: "Active", label: "Active" },
  { value: "Inactive", label: "Inactive" },
];

const DEFAULT_OPTIONS = [
  { value: "true", label: "Yes" },
  { value: "false", label: "No" },
];

const PAGE_SIZES = [10, 25, 50, 100];

const findOptionByValue = (options, value) =>
  options.find((option) => String(option.value) === String(value)) || null;

const COMPANY_PAGE_SIZE = 30;
const ALIAS_PAGE_SIZE = 30;
const DEFAULT_STATUS = "Active";
const LOGO_FILE_ACCEPT = "image/jpeg,image/png,image/gif,image/webp";
const LOGO_FILE_ERROR = "Please select a JPG, PNG, GIF, or WEBP image.";
const LOGO_PREVIEW_DEBOUNCE_MS = 500;
const DOMAIN_ERROR_MESSAGE =
  "Enter a domain name, such as google.com or yahoo.com, without https://, www., or anything after the domain.";

const isValidDomainOnly = (website) => {
  const value = (website || "").trim();
  if (!value) return true;
  if (/[^a-z0-9.-]/i.test(value)) return false;
  return /^(?!www\.)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/i.test(value);
};

const websiteLogoPreviewContainerStyle = {
  width: 42,
  height: 36,
  minWidth: 42,
  border: "1px solid var(--ct-border-color)",
  borderRadius: 4,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "var(--ct-tertiary-bg, #f8f9fa)",
};

const websiteLogoPreviewImageStyle = {
  width: 28,
  height: 28,
  objectFit: "contain",
};

const WebsiteLogoPreview = ({ logoUrl, fallbackLogoUrl }) => {
  const [logoLoadFailed, setLogoLoadFailed] = useState(false);
  const previewUrl = fallbackLogoUrl || (logoLoadFailed ? "" : logoUrl);

  useEffect(() => {
    setLogoLoadFailed(false);
  }, [logoUrl]);

  return (
    <div
      style={websiteLogoPreviewContainerStyle}
      aria-label="Website logo preview"
    >
      {previewUrl ? (
        <img
          key={previewUrl}
          src={previewUrl}
          alt="Website logo preview"
          style={websiteLogoPreviewImageStyle}
          onLoad={(e) => {
            e.currentTarget.style.display = "block";
          }}
          onError={(e) => {
            if (previewUrl === logoUrl) {
              setLogoLoadFailed(true);
            }
          }}
        />
      ) : (
        <span className="text-muted" style={{ fontSize: 10 }}>
          Logo
        </span>
      )}
    </div>
  );
};

const brandRowLogoImageStyle = {
  width: 32,
  height: 32,
  objectFit: "contain",
  border: "1px solid var(--ct-border-color)",
  borderRadius: 4,
  padding: 3,
};

const BrandRowLogo = ({ website, logo, brandName }) => {
  const [websiteLogoFailed, setWebsiteLogoFailed] = useState(false);
  const [fallbackLogoFailed, setFallbackLogoFailed] = useState(false);
  const websiteLogoUrl = buildCompanyLogoUrl(website);
  const logoUrl =
    logo && !fallbackLogoFailed
      ? logo
      : websiteLogoUrl && !websiteLogoFailed
        ? websiteLogoUrl
        : "";

  useEffect(() => {
    setWebsiteLogoFailed(false);
  }, [websiteLogoUrl]);

  useEffect(() => {
    setFallbackLogoFailed(false);
  }, [logo]);

  if (!logoUrl) {
    return <span className="text-muted">-</span>;
  }

  return (
    <img
      key={logoUrl}
      src={logoUrl}
      alt={`${brandName || "Brand"} logo`}
      style={brandRowLogoImageStyle}
      onError={() => {
        if (logoUrl === logo) {
          setFallbackLogoFailed(true);
          return;
        }

        setWebsiteLogoFailed(true);
      }}
    />
  );
};

const companySelectStyles = {
  control: (base, state) => {
    const borderColor = state.selectProps.invalid
      ? "var(--ct-danger)"
      : "var(--ct-input-border-color)";

    return {
      ...base,
      minHeight: "33px",
      borderColor,
      "&:hover": { borderColor },
    };
  },
  menu: (base) => ({ ...base, marginTop: 0, zIndex: 9999 }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const fetchCompanyPage = async (searchTerm, pageNumber) => {
  const res = await rpost(`${DEV_URL}/api/company/getall`, {
    PageNumber: pageNumber,
    PageSize: COMPANY_PAGE_SIZE,
    SearchTerm: searchTerm || null,
    CompanyID: null,
    ClientID: null,
    LicenseID: null,
    orderBy: null,
    StateCode: null,
    Commodity: null,
    LicenseType: null,
    ascendingOrder: true,
    LicenseStatus: null,
  });

  const json = res?.data;

  let items = [];

  if (json?.company && Array.isArray(json.company)) {
    items = json.company;
  } else if (Array.isArray(json)) {
    items = json;
  } else if (json?.data && Array.isArray(json.data)) {
    items = json.data;
  }

  return items.map((c) => ({
    value: c.id,
    label: c.legalName || "",
  }));
};

const fetchCompanyAliasOptions = async ({
  companyId = null,
  searchTerm = "",
  pageNumber = 1,
} = {}) => {
  const res = await rpost(`${DEV_URL}/api/company/GetAlias`, {
    PageNumber: pageNumber,
    PageSize: ALIAS_PAGE_SIZE,
    SearchTerm: searchTerm || null,
    CompanyID: companyId,
    ClientID: null,
    LicenseID: null,
    orderBy: "aliasName",
    StateCode: null,
    Commodity: null,
    LicenseType: null,
    ascendingOrder: true,
    HasCompany: 1,
  });

  const json = res?.data;
  const aliases = Array.isArray(json?.aliases)
    ? json.aliases
    : Array.isArray(json?.Aliases)
      ? json.Aliases
      : Array.isArray(json?.data?.aliases)
        ? json.data.aliases
        : Array.isArray(json?.data?.Aliases)
          ? json.data.Aliases
          : Array.isArray(json?.data)
            ? json.data
            : Array.isArray(json)
              ? json
              : [];

  return aliases
    .map((alias) => {
      const aliasName = alias?.aliasName || alias?.AliasName || "";
      const aliasId = alias?.id || alias?.Id || aliasName;
      const companyIdValue = alias?.companyId || alias?.CompanyId || "";
      const companyName = alias?.companyName || alias?.CompanyName || "";

      return {
        value: aliasId,
        label: aliasName,
        companyId: companyIdValue,
        companyName,
      };
    })
    .filter((option) => option.value && option.label);
};

const getErrorMessage = (response, fallback) =>
  response?.data?.message ||
  response?.data?.Message ||
  response?.data?.error ||
  fallback;

const getResponseConflicts = (response) =>
  response?.data?.conflicts || response?.data?.Conflicts || [];

const getResponseValidationType = (response) =>
  (response?.data?.validationType || response?.data?.ValidationType || "")
    .toString()
    .toLowerCase();

const getConflictMatchType = (conflict) =>
  (conflict?.matchType || conflict?.MatchType || "").toString().toLowerCase();

const getBrandNameConflicts = (response) =>
  getResponseConflicts(response).filter((conflict) =>
    ["brand", "brandname", "brand_name"].includes(getConflictMatchType(conflict))
  );

const hasBrandNameConflict = (response) =>
  getResponseValidationType(response) === "brandname" ||
  getBrandNameConflicts(response).length > 0;

const getDuplicateDomainConflicts = (response, website) => {
  const conflicts = getResponseConflicts(response);
  const validationType = getResponseValidationType(response);
  const domainConflicts = conflicts.filter(
    (conflict) =>
      (conflict?.matchType || conflict?.MatchType || "").toString().toLowerCase() ===
      "domain"
  );

  if (validationType !== "domain" && domainConflicts.length === 0) {
    return [];
  }

  const uniqueCompanies = new Map();
  domainConflicts.forEach((conflict) => {
    const companyId = conflict?.companyId || conflict?.CompanyId;
    if (!companyId || uniqueCompanies.has(String(companyId))) {
      return;
    }

    uniqueCompanies.set(String(companyId), {
      companyId: String(companyId),
      companyName:
        conflict?.companyName ||
        conflict?.CompanyName ||
        conflict?.legalName ||
        conflict?.LegalName ||
        "Company",
      companyStatus: conflict?.companyStatus || conflict?.CompanyStatus || "",
      domain:
        conflict?.matchedValue ||
        conflict?.MatchedValue ||
        conflict?.domain ||
        conflict?.Domain ||
        website ||
        "",
    });
  });

  return Array.from(uniqueCompanies.values());
};

const normalizeBrandValidationConflict = (conflict) => {
  const matchType = conflict?.matchType || conflict?.MatchType || "";
  const matchedValue = conflict?.matchedValue || conflict?.MatchedValue || "";
  const domain = conflict?.domain || conflict?.Domain || "";
  const brandName =
    conflict?.brandName ||
    conflict?.BrandName ||
    (String(matchType).toLowerCase() === "brand" ? matchedValue : "");

  return {
    companyId: conflict?.companyId || conflict?.CompanyId || "",
    companyName:
      conflict?.companyName ||
      conflict?.CompanyName ||
      conflict?.legalName ||
      conflict?.LegalName ||
      "Company",
    brandName,
    domain,
    matchType,
    matchedValue,
  };
};

const getWebsiteUrl = (website) => {
  const value = (website || "").trim();
  if (!value) return "";
  return /^https?:\/\//i.test(value) ? value : `https://${value}`;
};

const normalizeText = (value) => (value || "").trim().toLowerCase();

const normalizeStatusValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return DEFAULT_STATUS;
  }

  const normalized = String(value).trim().toLowerCase();
  return ["0", "false", "inactive"].includes(normalized) ? "Inactive" : "Active";
};

const getStatusLabel = (value) =>
  normalizeStatusValue(value);

const buildBrandFormData = ({ brandName, status, website, logo, companyId, isDefault }) => {
  const formData = new FormData();
  formData.append("BrandName", brandName.trim());
  formData.append("Status", normalizeStatusValue(status));
  formData.append("Website", (website || "").trim());
  formData.append("IsDefault", isDefault ? "true" : "false");

  if (logo) {
    formData.append("Logo", logo);
  }

  if (companyId) {
    formData.append("CompanyId", companyId);
  }

  return formData;
};

const Brand = () => {
  const snackbar = useShowSnackbar();
  const snackbarRef = useRef(snackbar);

  useEffect(() => {
    snackbarRef.current = snackbar;
  }, [snackbar]);

  const formatDate = (dateString) => {
    if (!dateString) return "";

    try {
      const formattedDate = trimInvalidUTCDate(dateString, false);
      return formattedDate || "";
    } catch (e) {
      return "";
    }
  };

  const { search, setSearch } = useSearch();
  const [appliedSearch, setAppliedSearch] = useState("");

  const [sortColumn, setSortColumn] = useState("");
  const [sortDirection, setSortDirection] = useState("asc");

  const [pageSize, setPageSize] = useState(50);
  const [currentPage, setCurrentPage] = useState(1);

  const [brandFilter, setBrandFilter] = useState("all");

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);

  const [selectedCompany, setSelectedCompany] = useState(null);
  const [draftCompany, setDraftCompany] = useState(null);
  const [draftAssignment, setDraftAssignment] = useState('all');
  const applyFilters = () => {
    setAppliedSearch(search.trim() || appliedSearch);
    setSearch('');
    setSelectedCompany(draftCompany);
    setBrandFilter(draftAssignment);
    setCurrentPage(1);
  };
  const companyPageRef = useRef(1);
  const companySearchRef = useRef("");
  const companyHasMoreRef = useRef(true);
  const [companyOptions, setCompanyOptions] = useState([]);
  const [companyLoading, setCompanyLoading] = useState(false);
  const searchTimerRef = useRef(null);

  const [moveSelectedCompany, setMoveSelectedCompany] = useState(null);
  const [moveCompanyOptions, setMoveCompanyOptions] = useState([]);
  const [moveCompanyLoading, setMoveCompanyLoading] = useState(false);
  const moveCompanyPageRef = useRef(1);
  const moveCompanySearchRef = useRef("");
  const moveCompanyHasMoreRef = useRef(true);
  const moveCompanySearchTimerRef = useRef(null);

  const [deleteModal, setDeleteModal] = useState({ show: false, row: null });
  const [deletingId, setDeletingId] = useState(null);
  const [checkingMappingId, setCheckingMappingId] = useState(null);

  const [editModal, setEditModal] = useState({ show: false, row: null });
  const [editBrandName, setEditBrandName] = useState("");
  const [editBrandNameError, setEditBrandNameError] = useState("");
  const [editStatus, setEditStatus] = useState(DEFAULT_STATUS);
  const [editWebsite, setEditWebsite] = useState("");
  const [editWebsiteLogoPreview, setEditWebsiteLogoPreview] = useState("");
  const [editLogo, setEditLogo] = useState(null);
  const [editLogoFallbackPreview, setEditLogoFallbackPreview] = useState("");
  const [editIsDefault, setEditIsDefault] = useState(false);
  const [saving, setSaving] = useState(false);

  const [moveModal, setMoveModal] = useState({
    show: false,
    row: null,
    fromMappedDelete: false,
  });
  const [moving, setMoving] = useState(false);

  const [showAddBrandModal, setShowAddBrandModal] = useState(false);
  const [newBrandName, setNewBrandName] = useState("");
  const [companyError, setCompanyError] = useState("");
  const [brandNameError, setBrandNameError] = useState("");
  const [newBrandStatus, setNewBrandStatus] = useState(DEFAULT_STATUS);
  const [newBrandWebsite, setNewBrandWebsite] = useState("");
  const [websiteError, setWebsiteError] = useState("");
  const [newBrandWebsiteLogoPreview, setNewBrandWebsiteLogoPreview] =
    useState("");
  const [newBrandLogo, setNewBrandLogo] = useState(null);
  const [newBrandLogoFallbackPreview, setNewBrandLogoFallbackPreview] =
    useState("");
  const [newBrandIsDefault, setNewBrandIsDefault] = useState(false);
  const [addingBrand, setAddingBrand] = useState(false);
  const [duplicateDomainModal, setDuplicateDomainModal] = useState({
    show: false,
    website: "",
    companies: [],
    source: null,
  });
  const [validationConflictModal, setValidationConflictModal] = useState({
    show: false,
    conflicts: [],
  });
  const [selectedDuplicateCompanyIds, setSelectedDuplicateCompanyIds] =
    useState({});
  const [resolvingDuplicateDomain, setResolvingDuplicateDomain] =
    useState(false);

  const [addBrandCompany, setAddBrandCompany] = useState(null);
  const [addBrandOptions, setAddBrandOptions] = useState([]);
  const [addBrandLoading, setAddBrandLoading] = useState(false);
  const [addBrandAlias, setAddBrandAlias] = useState(null);
  const [addBrandAliasOptions, setAddBrandAliasOptions] = useState([]);
  const [addBrandAliasLoading, setAddBrandAliasLoading] = useState(false);

  const addBrandPageRef = useRef(1);
  const addBrandSearchRef = useRef("");
  const addBrandHasMoreRef = useRef(true);
  const addBrandSearchTimerRef = useRef(null);
  const addBrandAliasPageRef = useRef(1);
  const addBrandAliasSearchRef = useRef("");
  const addBrandAliasHasMoreRef = useRef(true);
  const addBrandAliasSearchTimerRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setEditWebsiteLogoPreview(buildCompanyLogoUrl(editWebsite));
    }, LOGO_PREVIEW_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [editWebsite]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setNewBrandWebsiteLogoPreview(buildCompanyLogoUrl(newBrandWebsite));
    }, LOGO_PREVIEW_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [newBrandWebsite]);

  useEffect(() => {
    if (!editLogo) {
      setEditLogoFallbackPreview("");
      return undefined;
    }

    const objectUrl = URL.createObjectURL(editLogo);
    setEditLogoFallbackPreview(objectUrl);

    return () => URL.revokeObjectURL(objectUrl);
  }, [editLogo]);

  useEffect(() => {
    if (!newBrandLogo) {
      setNewBrandLogoFallbackPreview("");
      return undefined;
    }

    const objectUrl = URL.createObjectURL(newBrandLogo);
    setNewBrandLogoFallbackPreview(objectUrl);

    return () => URL.revokeObjectURL(objectUrl);
  }, [newBrandLogo]);

  const normalizeBrand = useCallback((item) => {
    const companyId = item.companyId || item.CompanyId || null;
    const companyName = item.companyName || item.CompanyName || "";

    return {
      id: item.id || item.Id,
      brandName: item.brandName || item.BrandName || "",
      status: normalizeStatusValue(
        item.status ??
        item.Status ??
        item.brandCompanyStatus ??
        item.BrandCompanyStatus
      ),
      logo: item.logo || item.Logo || "",
      website: item.website || item.Website || "",
      createdAt: item.createdAt || item.CreatedAt || "",
      deletedAt: item.deletedAt || item.DeletedAt || null,
      isDeleted: item.isDeleted === true || item.IsDeleted === true || false,
      brandCompanyId: item.brandCompanyId || item.BrandCompanyId || null,
      companyId,
      companyName: companyName || "Unassigned",
      brandCompanyStatus:
        item.brandCompanyStatus ?? item.BrandCompanyStatus ?? null,
      isDefault:
        item.isDefault === true ||
        item.IsDefault === true ||
        item.isDefault === 1 ||
        item.IsDefault === 1,
      brandCompanyCreatedAt:
        item.brandCompanyCreatedAt || item.BrandCompanyCreatedAt || null,
      isAssigned: Number(item.hasCompany ?? item.HasCompany ?? (companyId ? 1 : 0)) === 1,

    };
  }, []);

  const loadInitialCompanies = useCallback(async () => {
    setCompanyLoading(true);

    try {
      const options = await fetchCompanyPage("", 1);

      companyPageRef.current = 1;
      companySearchRef.current = "";
      companyHasMoreRef.current = options.length >= COMPANY_PAGE_SIZE;

      setCompanyOptions(options);
    } catch (e) {
      console.error("Failed to fetch companies", e);
    } finally {
      setCompanyLoading(false);
    }
  }, []);

  useEffect(() => {
    loadInitialCompanies();
  }, [loadInitialCompanies]);

  const handleCompanyInputChange = useCallback((inputValue) => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);

    searchTimerRef.current = setTimeout(async () => {
      companySearchRef.current = inputValue;
      companyPageRef.current = 1;
      companyHasMoreRef.current = true;

      setCompanyLoading(true);

      try {
        const options = await fetchCompanyPage(inputValue, 1);

        companyHasMoreRef.current = options.length >= COMPANY_PAGE_SIZE;
        setCompanyOptions(options);
      } catch (e) {
        console.error("Failed to search companies", e);
      } finally {
        setCompanyLoading(false);
      }
    }, 400);
  }, []);

  const handleCompanyScrollToBottom = useCallback(async () => {
    if (!companyHasMoreRef.current || companyLoading) return;

    setCompanyLoading(true);

    try {
      const nextPage = companyPageRef.current + 1;

      const moreOptions = await fetchCompanyPage(
        companySearchRef.current,
        nextPage
      );

      companyPageRef.current = nextPage;
      companyHasMoreRef.current = moreOptions.length >= COMPANY_PAGE_SIZE;

      setCompanyOptions((prev) => [...prev, ...moreOptions]);
    } catch (e) {
      console.error("Failed to load more companies", e);
    } finally {
      setCompanyLoading(false);
    }
  }, [companyLoading]);

  const filterMoveCompanies = useCallback(
    (options, sourceCompanyId = moveModal.row?.companyId) => {
      return sourceCompanyId
        ? options.filter((option) => option.value !== sourceCompanyId)
        : options;
    },
    [moveModal.row?.companyId]
  );

  const loadInitialMoveCompanies = useCallback(async (sourceCompanyId) => {
    setMoveCompanyLoading(true);

    try {
      const options = await fetchCompanyPage("", 1);

      moveCompanyPageRef.current = 1;
      moveCompanySearchRef.current = "";
      moveCompanyHasMoreRef.current = options.length >= COMPANY_PAGE_SIZE;

      setMoveCompanyOptions(filterMoveCompanies(options, sourceCompanyId));
    } catch (e) {
      console.error("Failed to fetch companies for transfer", e);
      setMoveCompanyOptions([]);
    } finally {
      setMoveCompanyLoading(false);
    }
  }, [filterMoveCompanies]);

  const handleMoveCompanyInputChange = useCallback(
    (inputValue, actionMeta) => {
      if (actionMeta?.action !== "input-change") {
        return inputValue;
      }

      if (moveCompanySearchTimerRef.current) {
        clearTimeout(moveCompanySearchTimerRef.current);
      }

      moveCompanySearchTimerRef.current = setTimeout(async () => {
        moveCompanySearchRef.current = inputValue;
        moveCompanyPageRef.current = 1;
        moveCompanyHasMoreRef.current = true;
        setMoveCompanyLoading(true);

        try {
          const options = await fetchCompanyPage(inputValue, 1);
          moveCompanyHasMoreRef.current =
            options.length >= COMPANY_PAGE_SIZE;
          setMoveCompanyOptions(filterMoveCompanies(options));
        } catch (e) {
          console.error("Failed to search companies for transfer", e);
          setMoveCompanyOptions([]);
        } finally {
          setMoveCompanyLoading(false);
        }
      }, 400);

      return inputValue;
    },
    [filterMoveCompanies]
  );

  const handleMoveCompanyScrollToBottom = useCallback(async () => {
    if (!moveCompanyHasMoreRef.current || moveCompanyLoading) {
      return;
    }

    setMoveCompanyLoading(true);

    try {
      const nextPage = moveCompanyPageRef.current + 1;
      const options = await fetchCompanyPage(
        moveCompanySearchRef.current,
        nextPage
      );

      moveCompanyPageRef.current = nextPage;
      moveCompanyHasMoreRef.current = options.length >= COMPANY_PAGE_SIZE;

      const filteredOptions = filterMoveCompanies(options);
      setMoveCompanyOptions((previousOptions) => {
        const existingValues = new Set(
          previousOptions.map((option) => option.value)
        );

        return [
          ...previousOptions,
          ...filteredOptions.filter(
            (option) => !existingValues.has(option.value)
          ),
        ];
      });
    } catch (e) {
      console.error("Failed to load more companies for transfer", e);
    } finally {
      setMoveCompanyLoading(false);
    }
  }, [moveCompanyLoading, filterMoveCompanies]);

  const loadAddBrandCompanies = useCallback(async () => {
    setAddBrandLoading(true);

    try {
      const options = await fetchCompanyPage("", 1);

      addBrandPageRef.current = 1;
      addBrandSearchRef.current = "";
      addBrandHasMoreRef.current = options.length >= COMPANY_PAGE_SIZE;

      setAddBrandOptions(options);
    } catch (e) {
      console.error("Failed to fetch companies for add brand", e);
    } finally {
      setAddBrandLoading(false);
    }
  }, []);

  const handleAddBrandInputChange = useCallback((inputValue) => {
    if (addBrandSearchTimerRef.current) {
      clearTimeout(addBrandSearchTimerRef.current);
    }

    addBrandSearchTimerRef.current = setTimeout(async () => {
      addBrandSearchRef.current = inputValue;
      addBrandPageRef.current = 1;
      addBrandHasMoreRef.current = true;

      setAddBrandLoading(true);

      try {
        const options = await fetchCompanyPage(inputValue, 1);

        addBrandHasMoreRef.current = options.length >= COMPANY_PAGE_SIZE;
        setAddBrandOptions(options);
      } catch (e) {
        console.error("Failed to search companies for add brand", e);
      } finally {
        setAddBrandLoading(false);
      }
    }, 400);
  }, []);

  const handleAddBrandScrollToBottom = useCallback(async () => {
    if (!addBrandHasMoreRef.current || addBrandLoading) return;

    setAddBrandLoading(true);

    try {
      const nextPage = addBrandPageRef.current + 1;

      const moreOptions = await fetchCompanyPage(
        addBrandSearchRef.current,
        nextPage
      );

      addBrandPageRef.current = nextPage;
      addBrandHasMoreRef.current = moreOptions.length >= COMPANY_PAGE_SIZE;

      setAddBrandOptions((prev) => [...prev, ...moreOptions]);
    } catch (e) {
      console.error("Failed to load more companies for add brand", e);
    } finally {
      setAddBrandLoading(false);
    }
  }, [addBrandLoading]);

  const loadAddBrandAliases = useCallback(
    async ({
      companyId = null,
      searchTerm = "",
      pageNumber = 1,
      replace = true,
    } = {}) => {
      setAddBrandAliasLoading(true);

      try {
        const options = await fetchCompanyAliasOptions({
          companyId,
          searchTerm,
          pageNumber,
        });

        addBrandAliasPageRef.current = pageNumber;
        addBrandAliasSearchRef.current = searchTerm;
        addBrandAliasHasMoreRef.current = options.length >= ALIAS_PAGE_SIZE;

        if (replace) {
          setAddBrandAliasOptions(options);
        } else {
          setAddBrandAliasOptions((prev) => {
            const existingValues = new Set(
              prev.map((option) => String(option.value))
            );

            return [
              ...prev,
              ...options.filter(
                (option) => !existingValues.has(String(option.value))
              ),
            ];
          });
        }

        return options;
      } catch (e) {
        console.error("Failed to fetch aliases for add brand", e);
        if (replace) {
          setAddBrandAliasOptions([]);
        }
        addBrandAliasHasMoreRef.current = false;
        return [];
      } finally {
        setAddBrandAliasLoading(false);
      }
    },
    []
  );

  const selectAddBrandCompanyOption = useCallback(
    (companyId, companyName) => {
      if (!companyId) {
        setAddBrandCompany(null);
        return;
      }

      const existingOption = addBrandOptions.find(
        (item) => String(item.value) === String(companyId)
      );
      const option =
        existingOption ||
        {
          value: companyId,
          label: companyName || "Selected Company",
        };

      if (!existingOption) {
        setAddBrandOptions((prev) => [option, ...prev]);
      }

      setAddBrandCompany(option);
      setCompanyError("");
    },
    [addBrandOptions]
  );

  const handleAddBrandCompanyChange = useCallback(
    async (val) => {
      setAddBrandCompany(val);
      if (val) setCompanyError("");
      setAddBrandAlias(null);

      await loadAddBrandAliases({
        companyId: val?.value || null,
        searchTerm: "",
        pageNumber: 1,
      });
    },
    [loadAddBrandAliases]
  );

  const handleAddBrandAliasChange = useCallback(
    (val) => {
      setAddBrandAlias(val);

      if (!val) {
        setAddBrandCompany(null);
        loadAddBrandAliases({
          companyId: null,
          searchTerm: "",
          pageNumber: 1,
        });
        return;
      }

      setNewBrandName(val.label);
      setBrandNameError("");
      selectAddBrandCompanyOption(val.companyId, val.companyName);
    },
    [loadAddBrandAliases, selectAddBrandCompanyOption]
  );

  const handleAddBrandAliasInputChange = useCallback(
    (inputValue, actionMeta) => {
      if (actionMeta?.action !== "input-change") {
        return inputValue;
      }

      if (addBrandAliasSearchTimerRef.current) {
        clearTimeout(addBrandAliasSearchTimerRef.current);
      }

      addBrandAliasSearchTimerRef.current = setTimeout(() => {
        loadAddBrandAliases({
          companyId: addBrandCompany?.value || null,
          searchTerm: inputValue,
          pageNumber: 1,
        });
      }, 400);

      return inputValue;
    },
    [addBrandCompany, loadAddBrandAliases]
  );

  const handleAddBrandAliasScrollToBottom = useCallback(async () => {
    if (!addBrandAliasHasMoreRef.current || addBrandAliasLoading) return;

    const nextPage = addBrandAliasPageRef.current + 1;
    await loadAddBrandAliases({
      companyId: addBrandCompany?.value || null,
      searchTerm: addBrandAliasSearchRef.current,
      pageNumber: nextPage,
      replace: false,
    });
  }, [addBrandAliasLoading, addBrandCompany, loadAddBrandAliases]);


  const fetchData = useCallback(async () => {
    setLoading(true);

    try {
      const res = await rpost(`${DEV_URL}/api/Brand/GetAll`, {
        PageNumber: currentPage,
        PageSize: pageSize,
        SearchTerm: appliedSearch || null,
        CompanyID: selectedCompany ? selectedCompany.value : null,
        ClientID: null,
        LicenseID: null,
        orderBy: sortColumn || "brandName",
        StateCode: null,
        Commodity: null,
        LicenseType: null,
        ascendingOrder: sortDirection === "asc",
        LicenseStatus: null,

        // Same as Alias filter:
        // All        => null
        // Assigned   => 1
        // Unassigned => 0
        HasCompany:
          brandFilter === "assigned"
            ? 1
            : brandFilter === "unassigned"
              ? 0
              : null,
      });

      if (!res || res.status < 200 || res.status >= 300) {
        snackbarRef.current(
          getErrorMessage(res, "Failed to fetch brands."),
          "error"
        );
        setData([]);
        setTotalRecords(0);
        return;
      }

      const json = res?.data;

      const brands = Array.isArray(json?.brands)
        ? json.brands
        : Array.isArray(json?.Brands)
          ? json.Brands
          : Array.isArray(json?.data)
            ? json.data
            : Array.isArray(json?.Data)
              ? json.Data
              : Array.isArray(json)
                ? json
                : [];

      setData(brands.map(normalizeBrand));

      setTotalRecords(
        json?.totalRecords ||
        json?.TotalRecords ||
        json?.totalCount ||
        json?.TotalCount ||
        json?.total ||
        json?.Total ||
        brands.length
      );
    } catch (e) {
      console.error("Failed to fetch brands", e);
      snackbarRef.current("Failed to fetch brands.", "error");
      setData([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  }, [
    selectedCompany,
    currentPage,
    pageSize,
    appliedSearch,
    sortColumn,
    sortDirection,
    brandFilter,
    normalizeBrand,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filteredData = data;

  const openDeleteConfirm = (row) => setDeleteModal({ show: true, row });

  const closeDeleteConfirm = () => {
    setDeleteModal({ show: false, row: null });
  };

  const getHasBrandApplicationMapping = async (brandId) => {
    const response = await rpost(
      `${DEV_URL}/api/company/AssetApplicationMapping`,
      {
        AssetId: brandId,
        AssetType: "Brand",
      }
    );

    if (
      !response ||
      response.status < 200 ||
      response.status >= 300 ||
      response?.data?.success === false ||
      response?.data?.Success === false
    ) {
      snackbar(
        response?.data?.message || "Unable to verify brand asset mapping",
        "error"
      );
      return null;
    }

    const resultData =
      response?.data?.data ?? response?.data?.Data ?? response?.data;
    const mappingResult = Array.isArray(resultData)
      ? resultData[0]
      : resultData;
    const hasApplicationMapping =
      mappingResult?.hasApplicationMapping ??
      mappingResult?.HasApplicationMapping ??
      false;

    return (
      hasApplicationMapping === true ||
      hasApplicationMapping === 1 ||
      String(hasApplicationMapping).toLowerCase() === "true"
    );
  };

  const handleDeleteBrandRequest = async (row) => {
    if (!row?.id) {
      snackbar("Unable to remove brand", "error");
      return;
    }

    try {
      setCheckingMappingId(row.id);
      const hasApplicationMapping = await getHasBrandApplicationMapping(row.id);

      if (hasApplicationMapping === null) {
        return;
      }

      if (hasApplicationMapping) {
        openMoveModal(row, true);
        return;
      }

      openDeleteConfirm(row);
    } catch (error) {
      console.error("Error checking brand asset mapping:", error);
      snackbar("An error occurred while checking brand asset mapping", "error");
    } finally {
      setCheckingMappingId(null);
    }
  };

  const handleConfirmDelete = async () => {
    const row = deleteModal.row;

    if (!row) return;

    try {
      setDeletingId(row.id);

      const response = await rdelete(`${DEV_URL}/api/Brand/${row.id}`);

      if (
        !response ||
        response.status < 200 ||
        response.status >= 300 ||
        response?.data?.success === false ||
        response?.data?.Success === false
      ) {
        snackbar(getErrorMessage(response, "Failed to remove brand."), "error");
        return;
      }

      await fetchData();
      closeDeleteConfirm();
      snackbar("Brand removed successfully", "success");
    } catch (e) {
      console.error("Failed to remove brand", e);
      snackbar("Failed to remove brand.", "error");
    } finally {
      setDeletingId(null);
    }
  };

  const openEditModal = (row) => {
    setEditBrandName(row.brandName);
    setEditBrandNameError("");
    setEditStatus(normalizeStatusValue(row.status));
    setEditWebsite(row.website || "");
    setWebsiteError("");
    setEditLogo(null);
    setEditIsDefault(!!row.isDefault);
    setEditModal({ show: true, row });
  };

  const closeEditModal = () => {
    setEditModal({ show: false, row: null });
    setEditBrandName("");
    setEditBrandNameError("");
    setEditStatus(DEFAULT_STATUS);
    setEditWebsite("");
    setWebsiteError("");
    setEditLogo(null);
    setEditIsDefault(false);
  };

  const openDuplicateDomainModal = (response, website, source) => {
    const companies = getDuplicateDomainConflicts(response, website);
    if (companies.length === 0) {
      return false;
    }

    setDuplicateDomainModal({
      show: true,
      website: website || companies[0]?.domain || "",
      companies,
      source,
    });
    setSelectedDuplicateCompanyIds({});
    return true;
  };

  const showBrandNameValidationSnackbar = (response, fallback) => {
    if (!hasBrandNameConflict(response)) {
      return false;
    }

    const message =
      getBrandNameConflicts(response).length > 0
        ? "Brand name already exists."
        : getErrorMessage(response, fallback);

    snackbar(message, "error");
    return true;
  };

  const openBrandValidationConflictModal = (response) => {
    if (!hasBrandNameConflict(response)) {
      return false;
    }

    const brandConflicts = getBrandNameConflicts(response);
    const conflicts =
      brandConflicts.length > 0 ? brandConflicts : getResponseConflicts(response);

    if (conflicts.length === 0) {
      return false;
    }

    setValidationConflictModal({
      show: true,
      conflicts: conflicts.map(normalizeBrandValidationConflict),
    });
    return true;
  };

  const closeValidationConflictModal = () => {
    setValidationConflictModal({
      show: false,
      conflicts: [],
    });
  };

  const fetchDuplicateBrandConflicts = async (brandName) => {
    const response = await rpost(`${DEV_URL}/api/Brand/GetAll`, {
      PageNumber: 1,
      PageSize: 100,
      SearchTerm: brandName,
      CompanyID: null,
      ClientID: null,
      LicenseID: null,
      orderBy: "brandName",
      StateCode: null,
      Commodity: null,
      LicenseType: null,
      ascendingOrder: true,
      LicenseStatus: null,
      HasCompany: null,
    });

    const json = response?.data;
    const brands = Array.isArray(json?.brands)
      ? json.brands
      : Array.isArray(json?.Brands)
        ? json.Brands
        : Array.isArray(json?.data)
          ? json.data
          : Array.isArray(json?.Data)
            ? json.Data
            : Array.isArray(json)
              ? json
              : [];

    const exactBrandName = normalizeText(brandName);
    const uniqueConflicts = new Map();

    brands
      .filter(
        (item) =>
          normalizeText(item.brandName || item.BrandName) === exactBrandName
      )
      .forEach((item) => {
        const companyId = item.companyId || item.CompanyId;
        const key = `${companyId || "unassigned"}-${item.id || item.Id || ""}`;
        if (uniqueConflicts.has(key)) {
          return;
        }

        uniqueConflicts.set(key, {
          matchType: "Brand",
          matchedValue: item.brandName || item.BrandName || brandName,
          brandName: item.brandName || item.BrandName || brandName,
          companyId,
          companyName:
            item.companyName ||
            item.CompanyName ||
            item.legalName ||
            item.LegalName ||
            "Company",
          domain: item.website || item.Website || "",
        });
      });

    return Array.from(uniqueConflicts.values()).filter((item) => item.companyId);
  };

  const openDuplicateBrandConflicts = (conflicts) =>
    openBrandValidationConflictModal({
      data: {
        validationType: "brandName",
        conflicts,
      },
    });

  const closeDuplicateDomainModal = () => {
    setDuplicateDomainModal({
      show: false,
      website: "",
      companies: [],
      source: null,
    });
    setSelectedDuplicateCompanyIds({});
  };

  const getCompanyDomainId = async (companyId, website) => {
    const normalizedWebsite = (website || "").trim().toLowerCase();
    if (!companyId || !normalizedWebsite) {
      return "";
    }

    const response = await rget(`${DEV_URL}/api/company/GetCompanyDomains/${companyId}`);
    const domains = Array.isArray(response?.data?.data)
      ? response.data.data
      : Array.isArray(response?.data?.Data)
        ? response.data.Data
        : [];
    const match = domains.find(
      (item) =>
        (item?.domain || item?.Domain || "").trim().toLowerCase() ===
        normalizedWebsite
    );

    return match?.id || match?.Id || "";
  };

  const handleSaveEdit = async () => {
    const row = editModal.row;

    if (!row) return;

    const nextBrandNameError = editBrandName.trim()
      ? ""
      : "Brand Name is required.";
    const nextWebsiteError = isValidDomainOnly(editWebsite)
      ? ""
      : DOMAIN_ERROR_MESSAGE;

    setEditBrandNameError(nextBrandNameError);
    setWebsiteError(nextWebsiteError);

    if (nextBrandNameError || nextWebsiteError) {
      return;
    }

    setSaving(true);

    try {
      const response = await rput(
        `${DEV_URL}/api/Brand/${row.id}`,
        buildBrandFormData({
          brandName: editBrandName,
          status: editStatus,
          website: editWebsite,
          logo: editLogo,
          companyId: row.companyId,
          isDefault: editIsDefault,
        })
      );

      if (
        !response ||
        response.status < 200 ||
        response.status >= 300 ||
        response?.data?.success === false ||
        response?.data?.Success === false
      ) {
        if (showBrandNameValidationSnackbar(response, "Failed to update brand.")) {
          return;
        }
        if (openDuplicateDomainModal(response, editWebsite, "edit")) {
          return;
        }
        snackbar(getErrorMessage(response, "Failed to update brand."), "error");
        return;
      }

      await fetchData();
      closeEditModal();
      snackbar("Brand updated successfully", "success");
    } catch (e) {
      console.error("Failed to update brand", e);
      if (showBrandNameValidationSnackbar(e?.response, "Failed to update brand.")) {
        return;
      }
      if (openDuplicateDomainModal(e?.response, editWebsite, "edit")) {
        return;
      }
      snackbar("Failed to update brand.", "error");
    } finally {
      setSaving(false);
    }
  };

  const openMoveModal = (row, fromMappedDelete = false) => {
    setMoveSelectedCompany(null);
    setMoveCompanyOptions([]);
    setMoveModal({ show: true, row, fromMappedDelete });
    loadInitialMoveCompanies(row?.companyId);
  };

  const closeMoveModal = () => {
    if (moveCompanySearchTimerRef.current) {
      clearTimeout(moveCompanySearchTimerRef.current);
      moveCompanySearchTimerRef.current = null;
    }

    setMoveModal({ show: false, row: null, fromMappedDelete: false });
    setMoveSelectedCompany(null);
    setMoveCompanyOptions([]);
    setMoveCompanyLoading(false);
  };

  const handleConfirmMove = async () => {
    const row = moveModal.row;

    if (!row) return;
    if (!moveSelectedCompany) {
      snackbar("Please select a target company", "error");
      return;
    }

    setMoving(true);

    try {
      const status = normalizeStatusValue(
        row.brandCompanyStatus ?? row.status
      );

      const response = await rpost(`${DEV_URL}/api/Brand/${row.id}/Transfer`, {
        SourceCompanyId: row.companyId || null,
        TargetCompanyId: moveSelectedCompany.value,
        Status: status,
      });

      if (
        !response ||
        response.status < 200 ||
        response.status >= 300 ||
        response?.data?.success === false ||
        response?.data?.Success === false
      ) {
        snackbar(getErrorMessage(response, "Failed to transfer brand."), "error");
        return;
      }

      await fetchData();
      closeMoveModal();
      snackbar("Brand transferred successfully", "success");
    } catch (e) {
      console.error("Failed to transfer brand", e);
      snackbar("Failed to transfer brand.", "error");
    } finally {
      setMoving(false);
    }
  };

  const handleAddBrand = async () => {
    const nextCompanyError = addBrandCompany ? "" : "Company is required.";
    const nextBrandNameError = newBrandName.trim()
      ? ""
      : "Brand Name is required.";
    const nextWebsiteError = isValidDomainOnly(newBrandWebsite)
      ? ""
      : DOMAIN_ERROR_MESSAGE;

    setCompanyError(nextCompanyError);
    setBrandNameError(nextBrandNameError);
    setWebsiteError(nextWebsiteError);

    if (nextCompanyError || nextBrandNameError || nextWebsiteError) {
      return;
    }

    setAddingBrand(true);

    try {
      const duplicateConflicts = await fetchDuplicateBrandConflicts(
        newBrandName.trim()
      );
      if (duplicateConflicts.length > 0) {
        openDuplicateBrandConflicts(duplicateConflicts);
        return;
      }

      const response = await rpost(
        `${DEV_URL}/api/Brand`,
        buildBrandFormData({
          brandName: newBrandName,
          status: newBrandStatus,
          website: newBrandWebsite,
          logo: newBrandLogo,
          companyId: addBrandCompany.value,
          isDefault: newBrandIsDefault,
        })
      );

      if (
        !response ||
        response.status < 200 ||
        response.status >= 300 ||
        response?.data?.success === false ||
        response?.data?.Success === false
      ) {
        if (openBrandValidationConflictModal(response)) {
          return;
        }
        if (openDuplicateDomainModal(response, newBrandWebsite, "add")) {
          return;
        }
        snackbar(getErrorMessage(response, "Failed to add brand."), "error");
        return;
      }

      snackbar(response?.data?.message || "Brand added successfully", "success");

      setShowAddBrandModal(false);
      setNewBrandName("");
      setCompanyError("");
      setBrandNameError("");
      setNewBrandStatus(DEFAULT_STATUS);
      setNewBrandWebsite("");
      setWebsiteError("");
      setNewBrandLogo(null);
      setNewBrandIsDefault(false);
      setAddBrandCompany(null);
      setAddBrandAlias(null);
      setAddBrandAliasOptions([]);

      await fetchData();
    } catch (e) {
      console.error("Failed to add brand", e);
      if (openBrandValidationConflictModal(e?.response)) {
        return;
      }
      if (openDuplicateDomainModal(e?.response, newBrandWebsite, "add")) {
        return;
      }
      snackbar("An error occurred while adding brand", "error");
    } finally {
      setAddingBrand(false);
    }
  };

  const handleDuplicateDomainConfirm = async () => {
    const selectedCompanies = duplicateDomainModal.companies.filter(
      (item) => selectedDuplicateCompanyIds[item.companyId]
    );

    if (selectedCompanies.length === 0) {
      snackbar("Please select at least one company", "error");
      return;
    }

    try {
      setResolvingDuplicateDomain(true);

      for (const item of selectedCompanies) {
        const domainId = await getCompanyDomainId(
          item.companyId,
          item.domain || duplicateDomainModal.website
        );

        if (!domainId) {
          snackbar(
            `Unable to find matching domain for ${item.companyName}`,
            "error"
          );
          return;
        }

        const response = await rpost(
          `${DEV_URL}/api/company/${item.companyId}/Domain/${domainId}`,
          {},
          { headers: { "Content-Type": "application/json" } }
        );

        if (
          !response ||
          response.status < 200 ||
          response.status >= 300 ||
          response?.data?.success === false ||
          response?.data?.Success === false
        ) {
          snackbar(
            response?.data?.message ||
            `Failed to remove domain from ${item.companyName}`,
            "error"
          );
          return;
        }
      }

      const retrySource = duplicateDomainModal.source;
      closeDuplicateDomainModal();
      snackbar(
        selectedCompanies.length === 1
          ? "Domain removed successfully"
          : "Domains removed successfully",
        "success"
      );
      await new Promise((resolve) => setTimeout(resolve, 0));

      if (retrySource === "edit") {
        await handleSaveEdit();
      } else {
        await handleAddBrand();
      }
    } catch (e) {
      console.error("Failed to resolve duplicate brand domain", e);
      snackbar("An error occurred while resolving duplicate domain", "error");
    } finally {
      setResolvingDuplicateDomain(false);
    }
  };

  const tablePagination = {
    pageIndex: Math.max(currentPage - 1, 0),
    pageSize,
  };

  const handleTablePaginationChange = (updater) => {
    const nextPagination =
      typeof updater === "function" ? updater(tablePagination) : updater;

    if (nextPagination.pageSize !== pageSize) {
      setPageSize(nextPagination.pageSize);
      setCurrentPage(1);
      return;
    }

    setCurrentPage((nextPagination.pageIndex ?? 0) + 1);
  };

  const tableSorting = sortColumn
    ? [{ id: sortColumn, desc: sortDirection === "desc" }]
    : [];

  const handleTableSortingChange = (updater) => {
    const nextSorting =
      typeof updater === "function" ? updater(tableSorting) : updater;
    const nextSort = nextSorting?.[0];

    setSortColumn(nextSort?.id || "");
    setSortDirection(nextSort?.desc ? "desc" : "asc");
    setCurrentPage(1);
  };

  const brandColumns = [
    {
      id: "logo",
      header: "",
      cell: ({ row }) => (
        <BrandRowLogo
          website={row.original.website}
          logo={row.original.logo}
          brandName={row.original.brandName}
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      header: "Brand",
      accessorKey: "brandName",
    },
    {
      header: "Website",
      accessorKey: "website",
      cell: ({ row }) =>
        row.original.website ? (
          <a href={getWebsiteUrl(row.original.website)} target="_blank" rel="noreferrer">
            {row.original.website}
          </a>
        ) : (
          <span className="text-muted">-</span>
        ),
    },
    {
      header: "Status",
      accessorKey: "status",
      cell: ({ row }) => (
        <span
          className={`badge bg-${normalizeStatusValue(row.original.status) === "Inactive" ? "secondary" : "success"
            }`}
        >
          {getStatusLabel(row.original.status)}
        </span>
      ),
    },
    {
      header: "Company",
      accessorKey: "companyName",
    },
    {
      header: "Created On",
      accessorKey: "createdAt",
      cell: ({ row }) => formatDate(row.original.createdAt),
    },
    {
      id: "actions",
      header: "Action",
      cell: ({ row: tableRow }) => {
        const row = tableRow.original;

        return (
          <Dropdown drop="down" align="end" className="position-static">
            <Dropdown.Toggle
              variant="link"
              className="btn btn-sm btn-link arrow-none p-0 shadow-none text-muted"
              style={{
                fontSize: "1.2rem",
                lineHeight: "1",
                cursor: "pointer",
              }}
            >
              &#x22EE;
            </Dropdown.Toggle>

            <Dropdown.Menu
              renderOnMount
              popperConfig={{ strategy: "fixed" }}
              style={{ zIndex: 9999, minWidth: "180px" }}
            >
              <Dropdown.Item
                onClick={() => openEditModal(row)}
                className="d-flex align-items-center gap-2"
                style={{
                  cursor: "pointer",
                  fontSize: "0.8rem",
                }}
              >
                <i className="uil uil-edit"></i>
                Edit Brand
              </Dropdown.Item>

              <Dropdown.Item
                onClick={() => openMoveModal(row)}
                className="d-flex align-items-center gap-2"
                style={{
                  cursor: "pointer",
                  fontSize: "0.8rem",
                }}
              >
                <i className="uil uil-exchange"></i>
                Transfer
              </Dropdown.Item>

              <Dropdown.Item
                onClick={() => handleDeleteBrandRequest(row)}
                className="d-flex align-items-center gap-2 text-danger"
                disabled={
                  deletingId === row.id ||
                  checkingMappingId === row.id
                }
                style={{
                  cursor:
                    deletingId === row.id ||
                      checkingMappingId === row.id
                      ? "not-allowed"
                      : "pointer",
                  fontSize: "0.8rem",
                }}
              >
                <i className="uil uil-trash"></i>
                {checkingMappingId === row.id
                  ? "Checking..."
                  : deletingId === row.id
                    ? "Removing..."
                    : "Remove"}
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        );
      },
      enableSorting: false,
    },
  ];

  return (
    <>
      <ConfirmationModal
        centered
        dialogClassName="modal-md"
        show={validationConflictModal.show}
        onHide={closeValidationConflictModal}
        title="Duplicate Brand / Domain"
        confirmButtonLabel="Close"
        confirmButtonVariant="secondary"
        onConfirm={closeValidationConflictModal}
      >
        <div className="table-responsive">
          <table className="table table-sm mb-0">
            <thead>
              <tr>
                <th>Associated Company</th>
                <th>Brand</th>
              </tr>
            </thead>
            <tbody>
              {validationConflictModal.conflicts.map((item, index) => (
                <tr key={`${item.companyId || "company"}-${index}`}>
                  <td>
                    {item.companyId ? (
                      <a
                        href={`/companies/${item.companyId}`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {item.companyName}
                      </a>
                    ) : (
                      item.companyName
                    )}
                  </td>
                  <td>
                    {item.brandName || item.matchedValue || item.domain || "-"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </ConfirmationModal>

      <ConfirmationModal
        centered
        dialogClassName="modal-md"
        show={duplicateDomainModal.show}
        onHide={closeDuplicateDomainModal}
        title="Duplicate Domain"
        confirmButtonLabel="Save"
        confirmButtonVariant="primary"
        isLoading={resolvingDuplicateDomain}
        onConfirm={handleDuplicateDomainConfirm}
      >
        <p className="mb-3">
          <strong>{duplicateDomainModal.website}</strong> is already associated
          with the following companies. Select the company associations to
          remove before saving this brand.
        </p>
        <div className="d-flex flex-column gap-2">
          {duplicateDomainModal.companies.map((item) => (
            <div key={item.companyId} className="border rounded p-2">
              <Form.Check
                id={`brand-page-duplicate-domain-${item.companyId}`}
                checked={Boolean(selectedDuplicateCompanyIds[item.companyId])}
                onChange={(event) =>
                  setSelectedDuplicateCompanyIds((current) => ({
                    ...current,
                    [item.companyId]: event.target.checked,
                  }))
                }
                label={
                  <span className="d-flex flex-column">
                    <a
                      href={`/companies/${item.companyId}`}
                      target="_blank"
                      rel="noreferrer"
                      className="link-color"
                    >
                      {item.companyName}
                    </a>
                    {item.companyStatus ? (
                      <small className="text-muted">{item.companyStatus}</small>
                    ) : null}
                  </span>
                }
              />
            </div>
          ))}
        </div>
      </ConfirmationModal>

      <ConfirmationModal
        centered
        dialogClassName="modal-md"
        show={deleteModal.show}
        onHide={closeDeleteConfirm}
        title="Remove Brand"
        confirmButtonLabel="Remove"
        confirmButtonVariant="danger"
        isLoading={!!deletingId}
        onConfirm={handleConfirmDelete}
      >
        <p>
          Are you sure you want to remove the brand{" "}
          <strong>{deleteModal.row?.brandName}</strong>?
        </p>
      </ConfirmationModal>

      <ConfirmationModal
        centered
        dialogClassName="modal-md"
        show={editModal.show}
        onHide={closeEditModal}
        title="Edit Brand"
        confirmButtonLabel={saving ? "Saving..." : "Save"}
        confirmButtonVariant="primary"
        isLoading={saving}
        onConfirm={handleSaveEdit}
      >
        <FormInput
          type="text"
          name="editBrandName"
          errors={
            editBrandNameError
              ? { editBrandName: { message: editBrandNameError } }
              : undefined
          }
          label={
            <>
              Brand Name <span className="text-danger" aria-hidden="true">*</span>
            </>
          }
          required
          containerClass="mb-3"
          value={editBrandName}
          onChange={(e) => {
            setEditBrandName(e.target.value);
            if (e.target.value.trim()) setEditBrandNameError("");
          }}
          placeholder="Enter brand name"
        />

        <Form.Group className="mb-3">
          <Form.Label>Website</Form.Label>
          <div className="d-flex align-items-start gap-2">
            <FormInput
              type="text"
              name="editWebsite"
              errors={
                websiteError
                  ? { editWebsite: { message: websiteError } }
                  : undefined
              }
              containerClass="flex-grow-1 mb-0"
              value={editWebsite}
              onChange={(e) => {
                setEditWebsite(e.target.value);
                if (isValidDomainOnly(e.target.value)) setWebsiteError("");
              }}
              placeholder="example.com"
            />
            <WebsiteLogoPreview
              logoUrl={editWebsiteLogoPreview}
              fallbackLogoUrl={
                editLogoFallbackPreview || editModal.row?.logo || ""
              }
            />
          </div>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Logo</Form.Label>
          {editModal.row?.logo && (
            <div className="mb-2">
              <img
                src={editModal.row.logo}
                alt={`${editModal.row.brandName} logo`}
                style={{
                  width: 48,
                  height: 48,
                  objectFit: "contain",
                  border: "1px solid var(--ct-border-color)",
                  borderRadius: 4,
                  padding: 4,
                }}
              />
            </div>
          )}
          <FileUploader
            value={editLogo ? [editLogo] : []}
            accept={LOGO_FILE_ACCEPT}
            onChange={(files) => setEditLogo(files[0] || null)}
            onRejected={() => snackbar(LOGO_FILE_ERROR, "error")}
            multiple={false}
            disabled={saving}
            customPlaceholder={<h5>Drop a logo here or click to upload.</h5>}
          />
        </Form.Group>

        <Form.Group>
          <Form.Label>Status</Form.Label>
          <Select
            options={STATUS_OPTIONS}
            value={findOptionByValue(STATUS_OPTIONS, editStatus)}
            onChange={(option) =>
              setEditStatus(option?.value ?? DEFAULT_STATUS)
            }
            isSearchable={false}
            isClearable={false}
          />
        </Form.Group>
        <Form.Group className="mt-3">
          <Form.Label>Default</Form.Label>
          <Select
            options={DEFAULT_OPTIONS}
            value={findOptionByValue(
              DEFAULT_OPTIONS,
              editIsDefault ? "true" : "false"
            )}
            onChange={(option) =>
              setEditIsDefault(option?.value === "true")
            }
            isSearchable={false}
            isClearable={false}
          />
        </Form.Group>
      </ConfirmationModal>

      <ConfirmationModal
        centered
        dialogClassName="modal-md"
        show={moveModal.show}
        onHide={closeMoveModal}
        title={moveModal.fromMappedDelete ? "Transfer Brand" : "Transfer"}
        confirmButtonLabel="Save"
        confirmButtonVariant="primary"
        isLoading={moving}
        onConfirm={handleConfirmMove}
      >
        {moveModal.fromMappedDelete ? (
          <p>
            This brand is used in another product for the same company, so you
            cannot remove it. You can transfer it to another company, or first
            delete/correct the product data, and then remove it.
          </p>
        ) : (
          <p>
            Moving brand <strong>{moveModal.row?.brandName}</strong> to a
            different company.
          </p>
        )}

        <Form.Group>
          <Form.Label>Select Target Company</Form.Label>
          <Select
            options={moveCompanyOptions}
            onMenuOpen={() => {
              if (moveCompanyOptions.length === 0) {
                loadInitialMoveCompanies(moveModal.row?.companyId);
              }
            }}
            onMenuScrollToBottom={handleMoveCompanyScrollToBottom}
            onInputChange={handleMoveCompanyInputChange}
            value={moveSelectedCompany}
            onChange={(val) => setMoveSelectedCompany(val)}
            placeholder="Search company..."
            isClearable
            isSearchable
            classNamePrefix="react-select"
            menuPortalTarget={document.body}
            styles={companySelectStyles}
            isLoading={moveCompanyLoading}
            maxMenuHeight={250}
          />
        </Form.Group>
      </ConfirmationModal>

      <ConfirmationModal
        centered
        dialogClassName="modal-md"
        show={showAddBrandModal}
        onHide={() => {
          setShowAddBrandModal(false);
          setCompanyError("");
          setBrandNameError("");
          setWebsiteError("");
          setNewBrandLogo(null);
          setNewBrandIsDefault(false);
          setAddBrandCompany(null);
          setAddBrandAlias(null);
          setAddBrandAliasOptions([]);
        }}
        title="Add Brand"
        confirmButtonLabel={addingBrand ? "Adding..." : "Add Brand"}
        confirmButtonVariant="primary"
        isLoading={addingBrand}
        onConfirm={handleAddBrand}
      >
        <Form.Group className="mb-3">
          <Form.Label htmlFor="add-brand-company">
            Company <span className="text-danger" aria-hidden="true">*</span>
          </Form.Label>
          <Select
            inputId="add-brand-company"
            options={addBrandOptions}
            onMenuScrollToBottom={handleAddBrandScrollToBottom}
            onInputChange={handleAddBrandInputChange}
            filterOption={null}
            value={addBrandCompany}
            onChange={handleAddBrandCompanyChange}
            placeholder="Search company..."
            isClearable
            isSearchable
            isLoading={addBrandLoading}
            classNamePrefix="react-select"
            menuPortalTarget={document.body}
            styles={companySelectStyles}
            maxMenuHeight={250}
            invalid={Boolean(companyError)}
            aria-required="true"
            aria-invalid={Boolean(companyError)}
            aria-describedby={companyError ? "add-brand-company-error" : undefined}
          />
          {companyError ? (
            <Form.Control.Feedback
              type="invalid"
              className="d-block"
              id="add-brand-company-error"
              role="alert"
            >
              {companyError}
            </Form.Control.Feedback>
          ) : null}
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Alias</Form.Label>
          <Select
            options={addBrandAliasOptions}
            onMenuScrollToBottom={handleAddBrandAliasScrollToBottom}
            onInputChange={handleAddBrandAliasInputChange}
            filterOption={null}
            value={addBrandAlias}
            onChange={handleAddBrandAliasChange}
            placeholder="Select alias..."
            isClearable
            isSearchable
            isLoading={addBrandAliasLoading}
            classNamePrefix="react-select"
            menuPortalTarget={document.body}
            styles={companySelectStyles}
            maxMenuHeight={250}
            noOptionsMessage={() => "No aliases found"}
          />
        </Form.Group>

        <FormInput
          type="text"
          name="newBrandName"
          errors={
            brandNameError
              ? { newBrandName: { message: brandNameError } }
              : undefined
          }
          label={
            <>
              Brand Name <span className="text-danger" aria-hidden="true">*</span>
            </>
          }
          required
          containerClass="mb-3"
          placeholder="Enter brand name"
          value={newBrandName}
          onChange={(e) => {
            setNewBrandName(e.target.value);
            if (e.target.value.trim()) setBrandNameError("");
          }}
        />

        <Form.Group className="mb-3">
          <Form.Label>Website</Form.Label>
          <div className="d-flex align-items-start gap-2">
            <FormInput
              type="text"
              name="newBrandWebsite"
              errors={
                websiteError
                  ? { newBrandWebsite: { message: websiteError } }
                  : undefined
              }
              containerClass="flex-grow-1 mb-0"
              placeholder="example.com"
              value={newBrandWebsite}
              onChange={(e) => {
                setNewBrandWebsite(e.target.value);
                if (isValidDomainOnly(e.target.value)) setWebsiteError("");
              }}
            />
            <WebsiteLogoPreview
              logoUrl={newBrandWebsiteLogoPreview}
              fallbackLogoUrl={newBrandLogoFallbackPreview}
            />
          </div>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Logo</Form.Label>
          <FileUploader
            value={newBrandLogo ? [newBrandLogo] : []}
            accept={LOGO_FILE_ACCEPT}
            onChange={(files) => setNewBrandLogo(files[0] || null)}
            onRejected={() => snackbar(LOGO_FILE_ERROR, "error")}
            multiple={false}
            disabled={addingBrand}
            customPlaceholder={<h5>Drop a logo here or click to upload.</h5>}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Status</Form.Label>
          <Select
            options={STATUS_OPTIONS}
            value={findOptionByValue(STATUS_OPTIONS, newBrandStatus)}
            onChange={(option) =>
              setNewBrandStatus(option?.value ?? DEFAULT_STATUS)
            }
            isSearchable={false}
            isClearable={false}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Default</Form.Label>
          <Select
            options={DEFAULT_OPTIONS}
            value={findOptionByValue(
              DEFAULT_OPTIONS,
              newBrandIsDefault ? "true" : "false"
            )}
            onChange={(option) =>
              setNewBrandIsDefault(option?.value === "true")
            }
            isSearchable={false}
            isClearable={false}
          />
        </Form.Group>
      </ConfirmationModal>

      <div className="d-flex justify-content-between align-items-center mb-3 mt-3">
        <h4
          className="header-title mb-0"
          style={{
            color: "#313a46",
            fontWeight: 600,
            fontSize: "1.125rem",
          }}
        >
        </h4>

        <div className="d-flex align-items-center gap-2">
          <LoadingButton type="button" variant="primary" size="sm" onClick={applyFilters}>Apply</LoadingButton>
          <LoadingButton
            variant="primary"
            size="sm"
            onClick={fetchData}
            disabled={loading}
            className="d-flex align-items-center gap-1"
          >
            <i className="uil uil-refresh"></i> Refresh
          </LoadingButton>

          <LoadingButton
            variant="primary"
            size="sm"
            className="d-flex align-items-center gap-1"
            onClick={() => {
              setShowAddBrandModal(true);
              setNewBrandName("");
              setCompanyError("");
              setBrandNameError("");
              setNewBrandStatus(DEFAULT_STATUS);
              setNewBrandWebsite("");
              setWebsiteError("");
              setNewBrandLogo(null);
              setNewBrandIsDefault(false);
              setAddBrandCompany(null);
              setAddBrandAlias(null);
              setAddBrandAliasOptions([]);
              loadAddBrandCompanies();
              loadAddBrandAliases();
            }}
          >
            <i className="uil uil-plus"></i> Add Brand
          </LoadingButton>
        </div>
      </div>

      <Card>
        <Card.Body style={{ overflow: "visible" }}>
          <div className="d-flex flex-wrap align-items-start mb-2 gap-2">
            <div style={{ maxWidth: 260, flexShrink: 0 }}>
              <TableSearch value={search} onChange={setSearch} onSearch={applyFilters}
                showAction={false} showApplied={false} />
            </div>

            <div style={{ minWidth: 220, flex: 1 }}>
              <Select
                options={companyOptions}
                onMenuScrollToBottom={handleCompanyScrollToBottom}
                onInputChange={handleCompanyInputChange}
                filterOption={null}
                value={draftCompany}
                onChange={setDraftCompany}
                placeholder="Filter by company..."
                isClearable
                isSearchable
                isLoading={companyLoading}
                classNamePrefix="react-select"
                menuPortalTarget={document.body}
                styles={companySelectStyles}
                maxMenuHeight={250}
              />
            </div>

            <div style={{ width: 130, flexShrink: 0 }}>
              <Select
                value={findOptionByValue(BRAND_FILTER_OPTIONS, draftAssignment)}
                options={BRAND_FILTER_OPTIONS}
                onChange={option => setDraftAssignment(option?.value || 'all')}
                isSearchable={false}
                isClearable={false}
              />
            </div>

          </div>
          <FilterPills filters={[
            { label: 'Search', value: appliedSearch, onRemove: () => { setAppliedSearch(''); setCurrentPage(1); } },
            { label: 'Company', value: selectedCompany?.label, onRemove: () => { setSelectedCompany(null); setDraftCompany(null); setCurrentPage(1); } },
            { label: 'Assignment', value: brandFilter === 'all' ? '' : findOptionByValue(BRAND_FILTER_OPTIONS, brandFilter)?.label,
              onRemove: () => { setBrandFilter('all'); setDraftAssignment('all'); setCurrentPage(1); } },
          ]} />

          <Table
            columns={brandColumns}
            data={filteredData}
            loading={loading}
            enableSearch={false}
            enablePagination
            enableHideColumns
            enableSorting
            enableFilters={false}
            enableSelection={false}
            enablePaginateAll={false}
            enableColumnVisibilityStorage={false}
            enableGlobalFilterStorage={false}
            enableUrlPagination={false}
            pageSizes={PAGE_SIZES}
            pagination={tablePagination}
            onPaginationChange={handleTablePaginationChange}
            sorting={tableSorting}
            onSortingChange={handleTableSortingChange}
            total={totalRecords}
            noDataMessage="No brands found."
          />
        </Card.Body>
      </Card>
    </>
  );
};

export default Brand;
